import { useState, useEffect } from 'react';
import { storage } from '../utils/storage';

export const useMoodData = () => {
  const [moodData, setMoodData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMoodData();
  }, []);

  const loadMoodData = () => {
    try {
      const saved = storage.get('moodData') || [];
      setMoodData(saved);
    } catch (error) {
      console.error('Error loading mood data:', error);
      setMoodData([]);
    } finally {
      setLoading(false);
    }
  };

  const addMoodEntry = (entry) => {
    const newEntry = {
      id: Date.now(),
      date: new Date().toISOString(),
      ...entry
    };
    
    const updated = [...moodData, newEntry];
    setMoodData(updated);
    storage.set('moodData', updated);
    return newEntry;
  };

  const updateMoodEntry = (id, updates) => {
    const updated = moodData.map(entry => 
      entry.id === id ? { ...entry, ...updates } : entry
    );
    setMoodData(updated);
    storage.set('moodData', updated);
  };

  const deleteMoodEntry = (id) => {
    const updated = moodData.filter(entry => entry.id !== id);
    setMoodData(updated);
    storage.set('moodData', updated);
  };

  const getTodaysMood = () => {
    const today = new Date().toDateString();
    return moodData.find(m => new Date(m.date).toDateString() === today) || null;
  };

  const getAverageMood = (days = null) => {
    let relevantData = moodData;
    
    if (days) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      relevantData = moodData.filter(m => new Date(m.date) >= cutoffDate);
    }
    
    if (relevantData.length === 0) return 0;
    
    const sum = relevantData.reduce((acc, m) => acc + m.mood, 0);
    return (sum / relevantData.length).toFixed(1);
  };

  const getMoodsByContext = () => {
    const contextMap = {};
    
    moodData.forEach(entry => {
      if (entry.contexts && entry.contexts.length > 0) {
        entry.contexts.forEach(context => {
          if (!contextMap[context]) {
            contextMap[context] = {
              moods: [],
              count: 0,
              total: 0
            };
          }
          contextMap[context].moods.push(entry.mood);
          contextMap[context].count++;
          contextMap[context].total += entry.mood;
        });
      }
    });
    
    return Object.entries(contextMap).map(([context, data]) => ({
      context,
      averageMood: (data.total / data.count).toFixed(1),
      count: data.count
    }));
  };

  const getMoodTrend = (days = 7) => {
    const trend = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayMoods = moodData.filter(m => 
        new Date(m.date).toDateString() === date.toDateString()
      );
      
      trend.push({
        date: date.toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' }),
        mood: dayMoods.length > 0 
          ? parseFloat((dayMoods.reduce((acc, m) => acc + m.mood, 0) / dayMoods.length).toFixed(1))
          : null,
        count: dayMoods.length
      });
    }
    
    return trend;
  };

  return {
    moodData,
    loading,
    addMoodEntry,
    updateMoodEntry,
    deleteMoodEntry,
    getTodaysMood,
    getAverageMood,
    getMoodsByContext,
    getMoodTrend,
    refresh: loadMoodData
  };
};